import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { DashboardStats } from '@/lib/types';
import { ELVIS_TIPS, blandaKortlek, visteDuKort, type ElvisKort, type ElvisSiffror } from '@/lib/elvisKort';

/**
 * Den mest ställda frågan citeras bara om JUST den går att visa: kort, utan
 * e-postadress, radbrytning eller långa sifferföljder (organisations- och
 * telefonnummer). Klarar den inte det visas inget sådant kort alls – att i
 * stället ta fråga nummer två hade gjort rubriken "mest ställda" falsk.
 */
function kanCiteras(text: string): boolean {
  return text.length < 70 && !text.includes('@') && !text.includes('\n') && !/\d{5,}|\d{6}-\d{4}/.test(text);
}

async function hamtaSiffror(): Promise<ElvisSiffror | null> {
  const [stats, topp, besvarade] = await Promise.all([
    supabase.rpc('dashboard_stats'),
    supabase.from('ai_questions').select('question_text, count').order('count', { ascending: false }).limit(1),
    // Bara besvarade luckor: close-gap sätter resolved_at även på avfärdade,
    // och "varje en sak jag kan svara på nu" gäller inte dem.
    supabase.from('ai_unanswered').select('id', { count: 'exact', head: true }).eq('gap_status', 'resolved'),
  ]);
  if (stats.error || !stats.data) return null;
  const d = stats.data as DashboardStats;
  const forsta = !topp.error ? (topp.data as Array<{ question_text: string; count: number }> | null)?.[0] : undefined;
  return {
    totalt: d.total_questions,
    unika: d.unique_questions,
    senaste7d: d.questions_last_7d,
    kallor: d.source_count,
    stycken: d.chunk_count,
    kretskampen: d.quiz_scores_total,
    // Ett fel ger inget kort – inte en nolla som ser ut som ett riktigt värde
    luckorBesvarade: besvarade.error ? null : besvarade.count,
    // question_text kan vara null i äldre rader – då inget kort, inte en krasch
    toppfraga:
      forsta && typeof forsta.question_text === 'string' && kanCiteras(forsta.question_text.trim())
        ? { text: forsta.question_text.trim(), antal: forsta.count }
        : null,
  };
}

/**
 * Hämtas en gång per sidladdning, oavsett hur många gånger man går till och
 * från ELvis-fliken (komponenten monteras om vid varje besök). Ett misslyckat
 * försök släpps, så att nästa hover kan prova igen.
 */
let siffrorLofte: Promise<ElvisSiffror | null> | null = null;
function siffrorEnGang(): Promise<ElvisSiffror | null> {
  if (!siffrorLofte) {
    siffrorLofte = hamtaSiffror()
      .catch(() => null)
      .then((s) => {
        if (!s) siffrorLofte = null;
        return s;
      });
  }
  return siffrorLofte;
}

/**
 * Kortleken till ELvis pratbubbla. Siffrorna hämtas första gången någon håller
 * muspekaren över honom. Fallerar hämtningen visas bara tipsen.
 */
export function useElvisKort(): { kort: ElvisKort; nasta: () => void } {
  const [lek, setLek] = useState<ElvisKort[]>(() => blandaKortlek(ELVIS_TIPS, []));
  const [index, setIndex] = useState(0);
  const begart = useRef(false);
  const forsta = useRef(true);
  // Siffrorna landar ofta medan bubblan redan syns. Den nya leken väntar här
  // och tas i bruk vid nästa hover, så att texten aldrig byts mitt under läsning.
  const vantande = useRef<ElvisKort[] | null>(null);

  const nasta = useCallback(() => {
    if (!begart.current) {
      begart.current = true;
      void siffrorEnGang().then((s) => {
        const visste = s ? visteDuKort(s) : [];
        if (visste.length > 0) vantande.current = blandaKortlek(ELVIS_TIPS, visste);
        else begart.current = false;
      });
    }
    if (forsta.current) {
      forsta.current = false;
      return;
    }
    if (vantande.current) {
      setLek(vantande.current);
      vantande.current = null;
      setIndex(0);
      return;
    }
    setIndex((i) => i + 1);
  }, []);

  return { kort: lek[index % lek.length], nasta };
}
